// The pack the workshop shows (the pack bar): every tab reads that pack's files. Remembered per browser.
import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { usePacks, WORKSHOP_PACK, type Pack } from "./data";

const KEY = "delve-workshop:pack";

interface PackContextValue { pack: string; packs: Pack[]; setPack(id: string): void; loading: boolean }

const PackContext = createContext<PackContextValue | null>(null);

function remembered(): string {
  try { return localStorage.getItem(KEY) ?? ""; } catch { return ""; }
}

export function PackProvider({ children }: { children: ReactNode }) {
  const query = usePacks();
  const [wanted, setWanted] = useState(remembered);
  const packs = useMemo(() => query.data ?? [], [query.data]);
  // A remembered pack that is no longer published falls back to the first one.
  const pack = packs.some((p) => p.id === wanted) ? wanted : packs[0]?.id ?? WORKSHOP_PACK;
  const value = useMemo<PackContextValue>(() => ({
    pack, packs, loading: query.isPending,
    setPack(id) {
      setWanted(id);
      try { localStorage.setItem(KEY, id); } catch { /* storage blocked */ }
    },
  }), [pack, packs, query.isPending]);
  return <PackContext.Provider value={value}>{children}</PackContext.Provider>;
}

export function usePack(): PackContextValue {
  const value = useContext(PackContext);
  if (!value) throw new Error("usePack outside <PackProvider>");
  return value;
}
